import { CommonModule } from '@angular/common';
import { Component, ElementRef, EventEmitter, HostListener, Input, OnDestroy, Output, ViewChild, computed, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { SocialNetworkOption } from '../../services/platform-social-links.service';

let nextId = 0;

/**
 * Selector de red social con buscador integrado (mismo patrón que
 * ngx-mat-select-search, sin depender de Angular Material): un botón compacto
 * con el ícono de la red y, al abrirlo, un panel con campo de búsqueda y la
 * lista filtrada. Navegable con teclado (flechas, Enter, Escape, Tab).
 * El panel usa position: fixed para no recortarse dentro de modales o listas
 * con overflow.
 */
@Component({
  selector: 'app-social-network-select',
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    <button #trigger type="button" class="nsel-trigger" [id]="triggerId" [class.open]="open()" [class.invalid]="invalid"
            (click)="toggle()" (keydown)="onTriggerKey($event)"
            aria-haspopup="listbox" [attr.aria-expanded]="open()" [attr.aria-controls]="open() ? listId : null" [attr.aria-label]="ariaLabel + ': ' + (selected()?.label || placeholder)">
      <span class="nsel-icon" *ngIf="selected() as sel; else noIcon" [style.--brand]="sel.color" [style.--on-brand]="sel.on_color"><i class="bi" [ngClass]="sel.icon" aria-hidden="true"></i></span>
      <ng-template #noIcon><span class="nsel-icon empty"><i class="bi bi-share" aria-hidden="true"></i></span></ng-template>
      <span class="nsel-value" [class.placeholder]="!selected()">{{ selected()?.label || placeholder }}</span>
      <i class="bi bi-chevron-down nsel-caret" aria-hidden="true"></i>
    </button>

    <div *ngIf="open()" #panel class="nsel-panel" [class.above]="openAbove()" [style.top.px]="openAbove() ? null : panelTop()" [style.bottom.px]="openAbove() ? panelBottom() : null" [style.left.px]="panelLeft()" [style.width.px]="panelWidth()" [style.max-height.px]="panelMaxHeight()">
      <div class="nsel-search">
        <i class="bi bi-search" aria-hidden="true"></i>
        <input #searchInput type="text" role="combobox" autocomplete="off" spellcheck="false" placeholder="Buscar red social..." data-no-limit-hint
               [attr.aria-controls]="listId" aria-expanded="true" aria-autocomplete="list" [attr.aria-activedescendant]="filtered().length ? listId + '-' + active() : null"
               [ngModel]="query()" (ngModelChange)="setQuery($event)" (keydown)="onSearchKey($event)" aria-label="Buscar red social" />
        <button *ngIf="query()" type="button" class="nsel-clear" (click)="setQuery(''); focusSearch()" aria-label="Limpiar búsqueda"><i class="bi bi-x-lg"></i></button>
      </div>
      <ul class="nsel-list" role="listbox" [id]="listId" #list [attr.aria-label]="ariaLabel">
        <li *ngFor="let opt of filtered(); let i = index; trackBy: trackOption" role="option" class="nsel-option"
            [id]="listId + '-' + i" [attr.aria-selected]="opt.key === value" [class.active]="i === active()" [class.selected]="opt.key === value"
            (mousedown)="$event.preventDefault()" (click)="choose(opt)" (mousemove)="active.set(i)">
          <span class="nsel-icon" [style.--brand]="opt.color" [style.--on-brand]="opt.on_color"><i class="bi" [ngClass]="opt.icon" aria-hidden="true"></i></span>
          <span class="nsel-option-label">{{ opt.label }}</span>
          <span class="nsel-used" *ngIf="used?.has(opt.key) && opt.key !== value">En uso</span>
          <i class="bi bi-check-lg nsel-check" *ngIf="opt.key === value" aria-hidden="true"></i>
        </li>
        <li *ngIf="!filtered().length" class="nsel-empty" role="presentation">
          <span>Sin resultados para "{{ query() }}".</span>
          <button type="button" class="nsel-fallback" (mousedown)="$event.preventDefault()" (click)="chooseKey('other')"><i class="bi bi-link-45deg"></i> Usar "Otra red"</button>
        </li>
      </ul>
    </div>
  `,
  styles: [`
    :host{position:relative;display:block;min-width:0}
    .nsel-trigger{display:flex;align-items:center;gap:.55rem;width:100%;height:var(--nsel-height,38px);min-height:var(--nsel-height,38px)!important;box-sizing:border-box;padding:0 .6rem 0 .35rem;color:var(--ts-text);border:1px solid var(--ts-border-strong);border-radius:10px;background:var(--ts-surface);font-size:.82rem!important;font-weight:650;text-align:left;cursor:pointer;transition:border-color var(--ts-transition),box-shadow var(--ts-transition)}
    .nsel-trigger:hover{border-color:color-mix(in srgb,var(--ts-primary) 55%,var(--ts-border-strong))}
    .nsel-trigger:focus-visible,.nsel-trigger.open{border-color:var(--ts-primary);box-shadow:0 0 0 3px color-mix(in srgb,var(--ts-primary) 22%,transparent);outline:none}
    .nsel-trigger.invalid{border-color:var(--ts-danger)}
    .nsel-icon{display:grid;place-items:center;width:26px;height:26px;flex:0 0 26px;color:var(--on-brand);border-radius:8px;background:var(--brand);font-size:.85rem}
    .nsel-icon.empty{color:var(--ts-text-subtle);background:var(--ts-surface-hover)}
    .nsel-value{flex:1;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
    .nsel-value.placeholder{color:var(--ts-text-subtle);font-weight:600}
    .nsel-caret{color:var(--ts-text-subtle);font-size:.72rem;transition:transform .2s ease}
    .nsel-trigger.open .nsel-caret{transform:rotate(180deg);color:var(--ts-primary)}

    .nsel-panel{position:fixed;z-index:2600;display:flex;flex-direction:column;overflow:hidden;color:var(--ts-text);border:1px solid var(--ts-border-strong);border-radius:12px;background:var(--ts-bg-elevated);box-shadow:0 18px 44px var(--ts-shadow-gray-1000-a45);animation:nsel-in .16s cubic-bezier(.2,.8,.2,1) both;transform-origin:top center}
    .nsel-panel.above{transform-origin:bottom center}
    .nsel-search{position:relative;display:flex;align-items:center;flex:0 0 auto;border-bottom:1px solid var(--ts-border)}
    .nsel-search>i{position:absolute;left:.75rem;color:var(--ts-text-subtle);font-size:.82rem;pointer-events:none}
    .nsel-search input{flex:1;min-width:0;height:42px;min-height:42px!important;padding:0 2.2rem 0 2.1rem!important;color:var(--ts-text);border:0!important;border-radius:0;background:transparent!important;box-shadow:none!important;font-size:.82rem!important;outline:none}
    .nsel-clear{position:absolute;right:.4rem;display:grid;place-items:center;width:26px;height:26px;min-height:26px!important;padding:0;color:var(--ts-text-subtle);border:0;border-radius:7px;background:transparent;font-size:.7rem;cursor:pointer}
    .nsel-clear:hover{color:var(--ts-text);background:var(--ts-surface-hover)}
    .nsel-list{flex:1 1 auto;min-height:0;margin:0;padding:.3rem;overflow-y:auto;list-style:none;overscroll-behavior:contain;scrollbar-width:thin}
    .nsel-option{display:flex;align-items:center;gap:.6rem;padding:.42rem .5rem;border-radius:8px;font-size:.8rem;font-weight:650;cursor:pointer;user-select:none}
    .nsel-option.active{background:var(--ts-surface-hover)}
    .nsel-option.selected{color:var(--ts-primary)}
    .nsel-option-label{flex:1;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
    .nsel-used{padding:.05rem .4rem;color:var(--ts-text-subtle);border:1px solid var(--ts-border);border-radius:999px;font-size:.58rem;font-weight:800}
    .nsel-check{color:var(--ts-primary);font-size:.9rem}
    .nsel-empty{display:grid;justify-items:center;gap:.5rem;padding:.9rem .6rem;color:var(--ts-text-muted);font-size:.76rem;text-align:center}
    .nsel-fallback{display:inline-flex;align-items:center;gap:.3rem;min-height:30px!important;height:30px;padding:0 .7rem;color:var(--ts-text);border:1px solid var(--ts-border-strong);border-radius:8px;background:var(--ts-surface);font-size:.74rem;cursor:pointer}
    @keyframes nsel-in{from{opacity:0;transform:translateY(-4px) scale(.98)}to{opacity:1;transform:none}}
    @media(prefers-reduced-motion:reduce){.nsel-panel{animation:none}.nsel-caret{transition:none}}
  `],
})
export class SocialNetworkSelectComponent implements OnDestroy {
  @Input() set options(value: SocialNetworkOption[] | null) { this.optionsSignal.set(value || []); }
  @Input() set value(key: string) { this.valueSignal.set(key || ''); }
  get value(): string { return this.valueSignal(); }
  /** Redes ya agregadas (se marcan "En uso", pero siguen disponibles). */
  @Input() used: ReadonlySet<string> | null = null;
  @Input() placeholder = 'Selecciona una red';
  @Input() ariaLabel = 'Red social';
  @Input() invalid = false;
  @Input() set triggerIdInput(id: string | null) { if (id) this.triggerId = id; }
  @Output() valueChange = new EventEmitter<string>();

  @ViewChild('trigger') private triggerRef?: ElementRef<HTMLButtonElement>;
  @ViewChild('searchInput') private searchRef?: ElementRef<HTMLInputElement>;
  @ViewChild('list') private listRef?: ElementRef<HTMLUListElement>;
  /** El panel se traslada a <body> al abrirse: un ancestro con transform,
   * filter o animación (modales, filas animadas) rompe position: fixed y lo
   * dejaría desplazado o recortado. Angular lo sigue gestionando igual y lo
   * elimina con el *ngIf al cerrarse. */
  @ViewChild('panel') set panelRef(ref: ElementRef<HTMLElement> | undefined) {
    this.panelEl = ref?.nativeElement ?? null;
    if (this.panelEl && this.panelEl.parentElement !== document.body) document.body.appendChild(this.panelEl);
  }
  private panelEl: HTMLElement | null = null;

  private readonly uid = ++nextId;
  triggerId = `nsel-trigger-${this.uid}`;
  readonly listId = `nsel-list-${this.uid}`;

  private readonly optionsSignal = signal<SocialNetworkOption[]>([]);
  private readonly valueSignal = signal('');
  readonly open = signal(false);
  readonly query = signal('');
  readonly active = signal(0);
  readonly panelTop = signal(0);
  readonly panelBottom = signal(0);
  readonly panelLeft = signal(0);
  readonly panelWidth = signal(260);
  readonly panelMaxHeight = signal(320);
  readonly openAbove = signal(false);

  readonly selected = computed(() => this.optionsSignal().find(o => o.key === this.valueSignal()) || null);
  readonly filtered = computed(() => {
    const q = this.normalize(this.query());
    const list = this.optionsSignal();
    if (!q) return list;
    // Primero las que empiezan con el texto, luego las que lo contienen.
    const starts = list.filter(o => this.normalize(o.label).startsWith(q) || o.key.startsWith(q));
    const contains = list.filter(o => !starts.includes(o) && (this.normalize(o.label).includes(q) || o.key.includes(q)));
    return [...starts, ...contains];
  });

  constructor(private readonly host: ElementRef<HTMLElement>) {}

  ngOnDestroy(): void {
    this.detachViewportListeners();
    this.panelEl?.remove();
  }

  trackOption(_: number, opt: SocialNetworkOption): string {
    return opt.key;
  }

  /** Abre el panel desde fuera (p. ej. al abrir un modal de creación). */
  openPanel(): void {
    if (this.open()) return;
    this.query.set('');
    const index = this.optionsSignal().findIndex(o => o.key === this.valueSignal());
    this.active.set(Math.max(0, index));
    this.position();
    this.open.set(true);
    this.attachViewportListeners();
    setTimeout(() => { this.focusSearch(); this.scrollActiveIntoView(); });
  }

  close(returnFocus = false): void {
    if (!this.open()) return;
    this.open.set(false);
    this.detachViewportListeners();
    if (returnFocus) this.triggerRef?.nativeElement.focus();
  }

  toggle(): void {
    this.open() ? this.close() : this.openPanel();
  }

  setQuery(value: string): void {
    this.query.set(value);
    this.active.set(0);
  }

  focusSearch(): void {
    this.searchRef?.nativeElement.focus({ preventScroll: true });
  }

  choose(opt: SocialNetworkOption): void {
    this.chooseKey(opt.key);
  }

  chooseKey(key: string): void {
    if (key !== this.valueSignal()) {
      this.valueSignal.set(key);
      this.valueChange.emit(key);
    }
    this.close(true);
  }

  onTriggerKey(event: KeyboardEvent): void {
    if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(event.key) && !this.open()) {
      event.preventDefault();
      this.openPanel();
    }
  }

  onSearchKey(event: KeyboardEvent): void {
    const total = this.filtered().length;
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (total) this.active.set((this.active() + 1) % total);
        this.scrollActiveIntoView();
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (total) this.active.set((this.active() - 1 + total) % total);
        this.scrollActiveIntoView();
        break;
      case 'Enter': {
        event.preventDefault();
        const opt = this.filtered()[this.active()];
        if (opt) this.choose(opt);
        break;
      }
      case 'Escape':
        event.preventDefault();
        event.stopPropagation();
        this.close(true);
        break;
      case 'Tab':
        this.close();
        break;
    }
  }

  @HostListener('document:mousedown', ['$event'])
  onDocumentMouseDown(event: MouseEvent): void {
    if (!this.open()) return;
    const target = event.target as Node;
    if (!this.host.nativeElement.contains(target) && !this.panelEl?.contains(target)) this.close();
  }

  private readonly reposition = () => this.position();

  private attachViewportListeners(): void {
    window.addEventListener('resize', this.reposition);
    window.addEventListener('scroll', this.reposition, true);
  }

  private detachViewportListeners(): void {
    window.removeEventListener('resize', this.reposition);
    window.removeEventListener('scroll', this.reposition, true);
  }

  /** Coloca el panel debajo del botón, o encima si no cabe. */
  private position(): void {
    const trigger = this.triggerRef?.nativeElement;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const margin = 8;
    const desired = 340;
    const below = window.innerHeight - rect.bottom - margin * 2;
    const above = rect.top - margin * 2;
    const placeAbove = below < 220 && above > below;
    const maxHeight = Math.max(180, Math.min(desired, placeAbove ? above : below));
    const width = Math.min(Math.max(rect.width, 250), window.innerWidth - margin * 2);
    const left = Math.min(Math.max(margin, rect.left), window.innerWidth - width - margin);
    this.openAbove.set(placeAbove);
    this.panelMaxHeight.set(maxHeight);
    this.panelWidth.set(width);
    this.panelLeft.set(left);
    this.panelTop.set(rect.bottom + 6);
    this.panelBottom.set(window.innerHeight - rect.top + 6);
  }

  private scrollActiveIntoView(): void {
    setTimeout(() => {
      const el = this.listRef?.nativeElement.querySelector<HTMLElement>('.nsel-option.active');
      el?.scrollIntoView({ block: 'nearest' });
    });
  }

  private normalize(value: string): string {
    return String(value || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').trim();
  }
}
